import { useContext, useEffect, useState } from 'react';
import InputConfig from '../../../../Components/InputConfig';
import DropDownSingle from '../../../../Components/DropDownSingle';
import { UnitsContext } from '../../../../Contexts/UnitsContext';
import { UserContext } from '../../../../Contexts/UserContext';
import ArrayInputControlledConfig from '../../../../Components/ArrayInputControlledConfig';
import CheckBoxInput from '../../../../Components/CheckBoxInput';
import PageHeading from '../../../../Components/PageHeading';
import useComonStyles from '../../../../commonStyles';

//mui imports====
import { Grid, Paper } from '@mui/material';
import CustomParentToChildNetworkMapping from './CustomParentToChildMapping';
import { FEATURE_DESCRIPTION } from '../../constant';
import AudioSwitchComponent from './AudioSwitch';

//===============

const GeneralSettings = () => {
	const {
		siteRawConfig,
		updateSiteRawConfig,
		mode,
		transformedGamIdsConfig,
		updateTransformedGamIds,
		customParentToChildMappingConfig,
		updateCustomParentToChildMappingConfig,
		aggressiveVpMuteCoverageConfig,
		updateAggressiveVpMuteCoverageConfig,
		dfpNetworkConfig
	} = useContext(UnitsContext);
	const { SITE_CONFIG } = FEATURE_DESCRIPTION;
	const { user } = useContext(UserContext);
	const [availableGamIdsAndSignifierList, setAvailableGamIdsAndSignifierList] = useState([]);

	const hbOnlyPostBiddingKey = 'hbOnlyPostBidding';
	const commonStyleClasses = useComonStyles();
	const gamNetworkIdsArray = Object.keys(dfpNetworkConfig?.gamNetworks || {});

	/**
	 * @name getAndSetAvailableGamIdsAndSignifierList
	 * @description This function is used to get the available gamIds list from the dfpNetworkConfig
	 * @returns {Object[]} - returns the available gamIds array of object with label, value and disable key
	 */
	const getAndSetAvailableGamIdsAndSignifierList = () => {
		if (!dfpNetworkConfig?.gamNetworks) return;

		const gamIdAndSignifierList = gamNetworkIdsArray.map((gamId) => {
			return {
				label: `${gamId}-${dfpNetworkConfig?.gamNetworks[gamId].signifier}`,
				value: gamId,
				disable: transformedGamIdsConfig.some((gamObject) => gamObject.value === gamId)
			};
		});

		setAvailableGamIdsAndSignifierList(gamIdAndSignifierList);
	};

	/**
	 * Updates the `hbOnlyPostBidding` configuration in the site raw configuration with a new value.
	 * @param {boolean} value - The new value to set for the `enabled` property of `hbOnlyPostBidding`.
	 */
	const updateHbPostBidding = (value) => {
		const clonedSiteRawConfig = structuredClone(siteRawConfig);
		clonedSiteRawConfig[hbOnlyPostBiddingKey] = clonedSiteRawConfig[hbOnlyPostBiddingKey] || {};

		clonedSiteRawConfig[hbOnlyPostBiddingKey].enabled = value;
		updateSiteRawConfig(clonedSiteRawConfig);
	};

	const updateAggressiveVpMuteCoverageCfg = (key, value) => {
		if (!key || value === null) {
			return;
		}

		const clonedSiteRawConfig = structuredClone(aggressiveVpMuteCoverageConfig);
		clonedSiteRawConfig[key] = value;
		updateAggressiveVpMuteCoverageConfig(clonedSiteRawConfig);
	};

	useEffect(() => {
		if (typeof dfpNetworkConfig === 'object' && Array.isArray(transformedGamIdsConfig)) {
			getAndSetAvailableGamIdsAndSignifierList();
		}
	}, [dfpNetworkConfig, transformedGamIdsConfig]);

	const tags = user?.tags;

	return (
		<>
			<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
				<PageHeading
					headingName="Site"
					headingPath={['Dashboard', 'Framerate', 'Site']}
					description="Manage all default site level settings like siteId, siteDomain etc."
					showInfoButton={true}
					infoMessage={SITE_CONFIG.GENERAL_SETTINGS}
				/>
				<Grid
					container
					spacing={2}
					style={{
						width: '95%',
						marginLeft: '3rem',
						marginTop: '3rem'
					}}
				>
					<Grid item xs={3}>
						<InputConfig
							label="Site Id"
							keyData="siteId"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
							editable={mode === 'CREATE'}
							inputType="number"
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Site Domain"
							keyData="siteDomain"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
							editable={mode === 'CREATE'}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Owner Email"
							keyData="ownerEmail"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
							editable={mode === 'CREATE'}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="MCM Id"
							keyData="mcmId"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
							editable={mode === 'CREATE'}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Video Player Id"
							keyData="videoPlayerId"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
							editable={mode === 'CREATE'}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputConfig
							label="Default Gam Id"
							keyData="gamId"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
							editable={mode === 'CREATE'}
						/>
					</Grid>
					<Grid item xs={3}>
						<DropDownSingle
							keyData="tag"
							list={tags}
							label="Tag"
							object={siteRawConfig}
							updateObject={updateSiteRawConfig}
						/>
					</Grid>

					<Grid item xs={12}>
						<ArrayInputControlledConfig
							label="Connected Gam Ids"
							directValue={transformedGamIdsConfig}
							useDropDownSingleMenu={true}
							dropDownOptions={availableGamIdsAndSignifierList}
							updateDirectValue={updateTransformedGamIds}
							inputGridStyle={{
								display: 'flex',
								padding: '1rem'
							}}
							keyData="connectedGamIds"
						/>
					</Grid>

					<Grid item xs={6}>
						<CheckBoxInput
							keyData={`${hbOnlyPostBiddingKey  }.enabled`}
							label="Hb Only (No Gam)"
							isObjectEditor={false}
							containerStyles={{ textWrap: 'nowrap', marginTop: '4rem' }}
							defaultChecked={false}
							directValue={siteRawConfig?.hbOnlyPostBidding?.enabled}
							updateDirectValue={(value) => updateHbPostBidding(value)}
						/>
					</Grid>

					<CustomParentToChildNetworkMapping
						customParentToChildMappingConfig={customParentToChildMappingConfig}
						updateCustomParentToChildMappingConfig={updateCustomParentToChildMappingConfig}
						transformedGamIdsConfig={transformedGamIdsConfig}
					/>

					<AudioSwitchComponent
						aggressiveVpMuteCoverageConfig={aggressiveVpMuteCoverageConfig}
						updateAggressiveVpMuteCoverageCfg={updateAggressiveVpMuteCoverageCfg}
					/>
				</Grid>
			</Paper>
		</>
	);
};

export default GeneralSettings;
